import { call, delay, put } from 'redux-saga/effects';
// api
import { apiApp } from '../../api/app';
import { apiResult } from '../../api/result';
// actions
import appAction from '../actions/app';
import resultAction from '../actions/result';
// types
import { editResultActionReturnType, getResultActionReturnType } from '../../types/actions/result';
//mapping
import { mapping } from '../../config/mapping';
import { normalizeStoredTestResult } from '../../tools/testResultEyes';
import { mergeSelfTestResultForSave } from '../../tools/mergeSelfTestResultForSave';
import { isInfantDyopsSession, isInfantTestType } from '../../tools/infantDefaults';
import { formatPatientDisplayName, sanitizeStoredPatientName } from '../../tools/patientDisplayName';

/** Resolves a patient display name from room user fields, then from a stored result string. */
function resolvePatientNameForResult(
  isIntegration: boolean,
  user: { firstName?: string; lastName?: string } | undefined,
  storedName?: string,
): string {
  if (isIntegration) {
    return '';
  }
  return formatPatientDisplayName(user) || sanitizeStoredPatientName(storedName);
}

export function* sagaGetResult(action: getResultActionReturnType): any {
  try {
    yield put(appAction.appLoading(true, 0));
    const resultResponse = yield call(apiApp.getRoomData, action.roomId);
    yield put(appAction.appLoading(true, 90));
    yield put(appAction.appLoading(true, 100));
    yield delay(500);
    const patientData = resultResponse.data.patientData ?? {};
    const monitorDyops = resultResponse.data.doctorData?.dyops;
    const normalized = normalizeStoredTestResult(resultResponse.data.testResult);
    const isInfantSelfTest =
      patientData.inputType === 'selfTest' &&
      (isInfantTestType(normalized.testType) || isInfantTestType(patientData.testType));
    const mergedForDisplay =
      patientData.inputType === 'selfTest' && monitorDyops && !isInfantSelfTest
        ? mergeSelfTestResultForSave(normalized, monitorDyops)
        : normalized;
    yield put(
      resultAction.getResult({
        ...mergedForDisplay,
        testType: mergedForDisplay.testType || patientData.testType,
        patientName: resolvePatientNameForResult(false, patientData?.user, mergedForDisplay.patientName),
        distance: String(mergedForDisplay.distance ?? '').trim() || patientData.distance,
        unit: mergedForDisplay.unit || patientData.unit,
        inputType: mergedForDisplay.inputType || patientData.inputType,
        patientId: mergedForDisplay.patientId || patientData?.user?.userId,
      }),
    );
    yield delay(10);
    yield put(appAction.appLoading(false, 0));
  } catch {
    yield put(appAction.appLoading(true, 100));
    yield delay(1000);
    yield put(appAction.appLoading(false, 0));
  }
}

export function* sagaEditResult(action: editResultActionReturnType): any {
  try {
    yield put(appAction.appLoading(true, 0));
    // get distance, unit measure and patient name from sockets before editing results
    const resultResponse = yield call(apiApp.getRoomData, action.roomId);
    const patientData = resultResponse.data.patientData ?? {};
    const monitorDyops = resultResponse.data.doctorData?.dyops;
    const isSelfTest = patientData.inputType === 'selfTest';
    const isInfantSelfTest = isSelfTest && isInfantDyopsSession(action.dyops, patientData.testType);
    const mergedResult =
      isSelfTest && !isInfantSelfTest
        ? mergeSelfTestResultForSave(action.dyops.result, monitorDyops, action.dyops)
        : action.dyops.result;
    const resultInfo = {
      ...mergedResult,
      ratio: action.isIntegration ? patientData.ratio : '',
      distance: String(patientData.distance ?? '').trim() || mergedResult.distance,
      patientName: resolvePatientNameForResult(action.isIntegration, patientData?.user, mergedResult.patientName),
      patientId: action.isIntegration ? '' : patientData?.user?.userId ?? mergedResult.patientId,
      doctorName: action.isIntegration ? '' : action.dyops.result.doctorName,
      unit: patientData.unit || mergedResult.unit,
      inputType: patientData.inputType || mergedResult.inputType,
      testType: mergedResult.testType || patientData.testType,
    };
    const result = mapping.buildPatientResultInfo(resultInfo);
    if (!action.isIntegration) {
      yield call(apiResult.editResult, action.roomId, result);
    }
    yield put(appAction.appLoading(true, 90));
    yield delay(10);
    action.patientCallback(action.isIntegration, result);
    yield put(appAction.appLoading(true, 100));
    yield delay(10);
    action.doctorCallback(action.isIntegration, result);
    yield put(appAction.appLoading(false, 0));
  } catch {
    // Request failed; loading state cleared in saga caller.
  }
}
